import moment from 'moment';
import type { Day, SubmitValidationErrorItem } from '../types';

/**
 * The pure date / arithmetic helpers lifted out of `mainMenu.jsx`.
 *
 * They were instance methods only because everything in that file was. None of them reads
 * `this`, so pulling them out changes nothing at runtime and makes them the one part of
 * the god component that is directly testable.
 */

/**
 * `mainMenu.jsx:weekAndYearConverter` — the ISO year/week the app uses everywhere.
 *
 * The class declared this method TWICE, at jsx:1794 and jsx:1870, with byte-identical
 * bodies; the second silently overwrote the first, so there was only ever one behavior.
 * That is reproduced here as the single definition it always effectively was.
 *
 * What it does: take the Monday, step forward three days to land mid-week, then read the
 * year and week off the END of that ISO week. The `W >= 52` guard walks the year back when
 * a January-1st week is still counted as week 52/53 of the previous year.
 *
 * Both values are returned as the strings `moment().format()` produced — `"2024"` and a
 * zero-padded `"07"` — because every caller wraps them in `parseInt`. Returning numbers
 * here would be tidier and would also change `parseInt`'s input from `"07"` to `7`; the
 * result is the same, but the string form is what the rest of the port compares against.
 */
export function weekAndYearConverter(date: string | undefined): { Y: string; W: string } {
  const yearOfFirstDay = moment(date).format('YYYY');

  const minimalDaysInFirstWeek = moment(date).add(3, 'days').format('YYYY-MM-DD');
  let Y = moment(minimalDaysInFirstWeek).endOf('isoWeek').format('YYYY');
  const W = moment(minimalDaysInFirstWeek).endOf('isoWeek').format('WW');

  // The original wrote `W >= 52` with W a string, which JavaScript resolves numerically
  // because the other operand is a number. `Number(W) >= 52` is that same comparison,
  // written so TypeScript can see it. `Y > yearOfFirstDay` stays a string comparison:
  // both are four-digit years, where lexicographic and numeric order agree.
  if (Y > yearOfFirstDay && Number(W) >= 52) {
    Y = yearOfFirstDay;
  }

  return { Y: Y, W: W };
}

/** `mainMenu.jsx:createListofDays` — the seven day cells of a week, Monday first. */
export function createListofDays(firstDayofCurrentWeek: string): Day[] {
  const days: Day[] = [];
  for (let i = 0; i < 7; i++) {
    days.push({
      day: i,
      date: moment(firstDayofCurrentWeek).add(i, 'days').format('YYYY-MM-DD'),
      timeTotal: 0,
      isSelected: 1,
      isHoliday: '',
      isLeave: '',
    });
  }
  return days;
}

/** `mainMenu.jsx:setDateAsNumber` — `YYYYMMDD`, compared as a string. */
export function setDateAsNumber(inputDate: Date): string {
  const year = inputDate.getFullYear();
  const month = (inputDate.getMonth() + 1).toString().padStart(2, '0'); // Month is zero-based, so add 1
  const day = inputDate.getDate().toString().padStart(2, '0');

  return `${year}${month}${day}`;
}

/**
 * `mainMenu.jsx:getMaximumNavigateDate` — the nearest Monday plus 28 days.
 *
 * Note this is NOT the same bound as `selectedDate.maxNextNavigateDate` (now + 28 days) or
 * as the calendar popup's own max (next Sunday + 28). Three different limits, as today.
 */
export function getMaximumNavigateDate(): Date {
  const now = new Date();
  const currentDay = now.getDay();
  const daysToMonday = currentDay === 0 ? 6 : 1 - currentDay; // Calculate days to Monday
  const mondayDate = new Date(now);
  mondayDate.setDate(now.getDate() + daysToMonday); // Set to the nearest Monday

  mondayDate.setDate(mondayDate.getDate() + 28);
  return mondayDate;
}

/**
 * `mainMenu.jsx:calculateTimePercentage` — the task progress bar, capped at 100.
 *
 * `loggedHours` is typed loosely because it genuinely arrives as both: a number straight
 * from the BFF, or the STRING that `formatNumberToTwoDecimals` returns after an edit
 * (jsx:1195, jsx:1363). Division coerces either the same way.
 */
export function calculateTimePercentage(
  loggedHours: number | string | undefined,
  estimatedHours: number | undefined,
): number {
  let value = 0;
  // (loggedHours / estimatedHours) * 100
  if (estimatedHours !== undefined && estimatedHours > 0) {
    value = (Number(loggedHours) / estimatedHours) * 100;
    if (value > 100) {
      value = 100;
    }
  }
  if (estimatedHours === 0) {
    value = 0;
  }
  value = Math.round((value + Number.EPSILON) * 100) / 100;
  return parseFloat(String(value));
}

/**
 * `mainMenu.jsx:formatNumberToTwoDecimals` — two decimals, trailing zeros trimmed.
 *
 * Returns a STRING, and its result is written straight back into `taskData.loggedHours`.
 * That is why `calculateTimePercentage` above has to accept one.
 */
export function formatNumberToTwoDecimals(value: number | string | undefined): string {
  const formattedValue = parseFloat(String(value)).toFixed(2);
  return formattedValue.replace(/\.00$/, '').replace(/(\.\d*?)0+$/, '$1');
}

/**
 * `Date` reached through a signature TypeScript does not model: the original spread an
 * array of date parts into the constructor (`new Date(...a.date.split("/").reverse())`).
 */
const DateFromParts = Date as unknown as new (...args: unknown[]) => Date;

/**
 * `mainMenu.jsx:getSortedListByDate` — flattens `[{ "01/02/2024": 8 }, …]` into
 * `[{ date, value }, …]` ordered by date, for the submit-validation error modal.
 *
 * The sort key is the original's, bug included: `"DD/MM/YYYY"` reversed gives
 * `[YYYY, MM, DD]`, and the three-argument `Date` constructor treats the middle value as a
 * ZERO-based month — so every key lands one month late. Every entry shifts by the same
 * month, so the resulting order is unaffected, and these values are never rendered as
 * dates. Fixing it would be a change with no visible effect; it is left alone.
 *
 * `.getTime()` is what JavaScript's `dateA - dateB` does internally, NaN included.
 */
export function getSortedListByDate(
  list: Record<string, unknown>[] | undefined,
): SubmitValidationErrorItem[] {
  let sortedList: SubmitValidationErrorItem[] = [];
  list?.map((obj) => {
    sortedList.push({
      date: Object.keys(obj)[0] as string,
      value: Object.values(obj)[0] as string | number,
    });
  });

  sortedList = sortedList.sort(
    (a, b) =>
      new DateFromParts(...a.date.split('/').reverse()).getTime() -
      new DateFromParts(...b.date.split('/').reverse()).getTime(),
  );

  return sortedList;
}
